/**
 * Beállítások — Cég-profil
 */
import { useState, useEffect, useRef } from 'react';
import { get, post } from '../../lib/api';
import { PageHeader, Button, Card, Input } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';

export default function CompanySettingsPage() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoId, setLogoId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    name: '', taxNumber: '', postalCode: '', city: '', street: '', houseNumber: '',
    bankAccount: '', email: '', phone: '',
    defaultInvoiceNotes: '', defaultProformaNotes: '', defaultDeliveryNoteNotes: '',
    deliveryNoteShowPrices: false,
    cashRequireAuthorizer: false,
    cashRequireCashier: false,
  });

  useEffect(() => {
    (async () => {
      try {
        const data = await get<any>('/company-profile');
        if (data) {
          setForm({
            name: data.company_name || data.name || '',
            taxNumber: data.tax_number || '',
            postalCode: data.address_postal_code || '',
            city: data.address_city || '',
            street: data.address_street_name || '',
            houseNumber: data.address_house_number || '',
            bankAccount: data.bank_account_primary || '',
            email: data.email || '',
            phone: data.phone || '',
            defaultInvoiceNotes: data.default_invoice_notes || '',
            defaultProformaNotes: data.default_proforma_notes || '',
            defaultDeliveryNoteNotes: data.default_delivery_note_notes || '',
            deliveryNoteShowPrices: data.delivery_note_show_prices || false,
            cashRequireAuthorizer: data.cash_require_authorizer || false,
            cashRequireCashier: data.cash_require_cashier || false,
          });
          if (data.logo_document_id) setLogoId(data.logo_document_id);
        }
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, []);

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [key]: e.target.value }));

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await post('/setup/company', form);
      toast('Cégadatok mentve.', 'success');
    } catch (err: any) {
      toast(err.message || 'Mentés sikertelen.', 'error');
    } finally { setSaving(false); }
  }

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--slate)' }}>Betöltés...</div>;

  return (
    <div>
      <PageHeader title="Cég-profil" description="Cégadatok módosítása — ezek jelennek meg a számláidon." />

      {/* Logó feltöltés */}
      <Card className="mb-4">
        <div className="card-h"><h3>Számla-logó</h3></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ width: '120px', height: '60px', border: '1px dashed var(--line)', borderRadius: 'var(--r)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--mist)', overflow: 'hidden' }}>
            {logoId ? (
              <img src={`/api/documents/${logoId}/preview`} alt="Logó" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={e => (e.currentTarget.style.display = 'none')} />
            ) : (
              <span style={{ fontSize: '11px', color: 'var(--slate)' }}>Nincs logó</span>
            )}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setUploading(true);
              try {
                const fd = new FormData();
                fd.append('logo', file);
                const token = localStorage.getItem('accessToken');
                const res = await fetch('/api/company-profile/logo', { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd });
                const data = await res.json();
                if (res.ok) { setLogoId(data.logoDocumentId); toast('Logó feltöltve.', 'success'); }
                else { toast(data.error || 'Feltöltés sikertelen.', 'error'); }
              } catch { toast('Feltöltés sikertelen.', 'error'); } finally { setUploading(false); }
            }} />
            <Button size="sm" variant="ghost" loading={uploading} onClick={() => fileRef.current?.click()}>Logó feltöltése</Button>
            <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '4px' }}>PNG, JPG vagy WEBP, max 10 MB. Ha nagyobb mint 800px, automatikusan kicsinyítjük.<br/>Megjelenik a számlákon, szállítóleveleken és a rendszer bal felső sarkában.</div>
          </div>
        </div>
      </Card>

      <Card>
        <form onSubmit={handleSave} className="space-y-4">
          <Input label="Cégnév *" value={form.name} onChange={set('name')} placeholder="Példa Kft." />
          <Input label="Adószám *" value={form.taxNumber} onChange={set('taxNumber')} placeholder="12345678-2-43" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
            <Input label="Irányítószám *" value={form.postalCode} onChange={set('postalCode')} placeholder="1051" />
            <Input label="Város *" value={form.city} onChange={set('city')} placeholder="Budapest" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
            <Input label="Közterület neve *" value={form.street} onChange={set('street')} placeholder="Váci utca" />
            <Input label="Házszám *" value={form.houseNumber} onChange={set('houseNumber')} placeholder="1." />
          </div>
          <Input label="Bankszámlaszám *" value={form.bankAccount} onChange={set('bankAccount')} placeholder="12345678-12345678-12345678" />
          <Input label="E-mail" type="email" value={form.email} onChange={set('email')} placeholder="info@ceged.hu" />
          <Input label="Telefonszám" value={form.phone} onChange={set('phone')} placeholder="+36 1 234 5678" />

          <div style={{ borderTop: '1px solid var(--line)', paddingTop: '16px', marginTop: '8px' }}>
            <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px', color: 'var(--ink)' }}>Alapértelmezett megjegyzések</h4>
            <div style={{ fontSize: '11px', color: 'var(--slate)', marginBottom: '12px' }}>Ezek a szövegek automatikusan kitöltődnek az új dokumentumokon, de minden űrlapon szerkeszthetők.</div>

            <label className="form-label">Számla megjegyzés</label>
            <textarea value={form.defaultInvoiceNotes} onChange={e => setForm(f => ({ ...f, defaultInvoiceNotes: e.target.value }))} rows={2} className="form-input" placeholder="Pl. Köszönjük a megrendelést!" style={{ marginBottom: '12px' }} />

            <label className="form-label">Díjbekérő megjegyzés</label>
            <textarea value={form.defaultProformaNotes} onChange={e => setForm(f => ({ ...f, defaultProformaNotes: e.target.value }))} rows={2} className="form-input" placeholder="Pl. A számlát a fizetés után állítjuk ki." style={{ marginBottom: '12px' }} />

            <label className="form-label">Szállítólevél megjegyzés</label>
            <textarea value={form.defaultDeliveryNoteNotes} onChange={e => setForm(f => ({ ...f, defaultDeliveryNoteNotes: e.target.value }))} rows={2} className="form-input" placeholder="Pl. Kérjük, ellenőrizze a csomagot átvételkor!" />

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', cursor: 'pointer', fontSize: '13px' }}>
              <input type="checkbox" checked={form.deliveryNoteShowPrices} onChange={e => setForm(f => ({ ...f, deliveryNoteShowPrices: e.target.checked }))}
                style={{ width: '16px', height: '16px', accentColor: 'var(--navy)' }} />
              <span>Szállítólevélen árak megjelenítése</span>
            </label>
            <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '4px', marginLeft: '24px' }}>Ha bekapcsolod, a szállítólevél egységárat, ÁFA-t és összesítőt is tartalmaz (mint egy számla). Egyedi szállítólevélenként is felülírható.</div>
          </div>

          <div style={{ borderTop: '1px solid var(--line)', paddingTop: '16px', marginTop: '8px' }}>
            <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px', color: 'var(--ink)' }}>Házipénztár beállítások</h4>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', marginBottom: '8px' }}>
              <input type="checkbox" checked={form.cashRequireAuthorizer} onChange={e => setForm(f => ({ ...f, cashRequireAuthorizer: e.target.checked }))}
                style={{ width: '16px', height: '16px', accentColor: 'var(--navy)' }} />
              <span>Utalványozó kötelező a pénztárbizonylatokon</span>
            </label>
            <div style={{ fontSize: '11px', color: 'var(--slate)', marginBottom: '12px', marginLeft: '24px' }}>Ha bekapcsolod, minden bizonylat kiállításánál ki kell választani az utalványozó személyt.</div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
              <input type="checkbox" checked={form.cashRequireCashier} onChange={e => setForm(f => ({ ...f, cashRequireCashier: e.target.checked }))}
                style={{ width: '16px', height: '16px', accentColor: 'var(--navy)' }} />
              <span>Pénztáros kötelező a pénztárbizonylatokon</span>
            </label>
            <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '4px', marginLeft: '24px' }}>Ha bekapcsolod, minden bizonylat kiállításánál ki kell választani a pénztáros személyt.</div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', paddingTop: '12px' }}>
            <Button type="submit" loading={saving}>Mentés</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
